<template>
  <v-dialog
    max-width="600"
    v-model="show"
    persistent
    data-testid="generate-coupons-dialog"
  >
    <v-card class="pa-2">
      <v-card-title>
        <div class="d-flex align-center justify-center">
          <div class="md-subheader-no-spacing">
            Generate Coupons
          </div>
          <v-spacer />
          <v-btn
            style="left: 15px"
            icon
            variant="plain"
            @click="cancel"
            data-testid="generate-coupons-close"
          ><v-icon icon="close" /></v-btn>
        </div>
        <div class="md-body-no-spacing">
          <div>
            Generate a batch of unique coupon codes that all start with the
            same prefix.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <v-form v-model="valid" ref="form" @submit.prevent="submit">
          <v-text-field
            label="Prefix"
            v-model="prefix"
            :rules="[validateNotSpaces]"
            data-testid="generate-coupons-prefix"
          />
          <v-text-field
            label="Count"
            type="number"
            v-model="count"
            :rules="[validateNotEmpty, validateInteger, validateGreaterThanZero, validateMax(maxCount)]"
            data-testid="generate-coupons-count"
          />
          <v-select
            label="Coupon Type"
            v-model="type"
            :items="['Percentage', 'Flat Rate']"
            :rules="[validateNotEmpty]"
            data-testid="generate-coupons-type"
          />
          <v-text-field
            v-if="type === 'Percentage'"
            label="Percentage"
            type="number"
            prefix="%"
            v-model="percentageValue"
            :rules="[validateNotEmpty, validateInteger, validatePercent]"
            data-testid="generate-coupons-percentage"
          />
          <v-text-field
            v-else
            label="Flat Rate"
            type="number"
            prefix="$"
            v-model="flatRateValue"
            :rules="[validateNotEmpty, validateAllowance]"
            data-testid="generate-coupons-flat-rate"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          variant="text"
          @click="cancel"
          data-testid="generate-coupons-cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="text"
          @click="generate"
          :disabled="!valid"
          data-testid="generate-coupons-generate"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import rules from "@/mixins/rules";
import { generateCoupons } from "./couponHelpers.js";

const MAX_COUNT = 1000;
const DEFAULT_COUNT = 50;

export default {
  name: "GenerateCouponsDialog",

  props: {
    existingCodes: {
      type: Array,
      default: () => [],
    },
  },

  mixins: [rules],

  emits: ["generated", "cancel"],

  data() {
    return {
      show: false,
      valid: false,
      prefix: "",
      count: DEFAULT_COUNT,
      type: "Percentage",
      percentageValue: null,
      flatRateValue: null,
      maxCount: MAX_COUNT,
    };
  },

  methods: {
    open() {
      this.prefix = "";
      this.count = DEFAULT_COUNT;
      this.type = "Percentage";
      this.percentageValue = null;
      this.flatRateValue = null;
      this.show = true;
      this.$nextTick(() => {
        if (this.$refs.form && typeof this.$refs.form.reset === "function") {
          this.$refs.form.reset();
        }
        // reset() clears count back to null via the form; restore the
        // default so the field shows 50 rather than blank on open.
        this.count = DEFAULT_COUNT;
      });
    },

    generate() {
      const value =
        this.type === "Percentage"
          ? Number(this.percentageValue)
          : Number(this.flatRateValue);

      let coupons;
      try {
        coupons = generateCoupons({
          prefix: this.prefix,
          count: Number(this.count),
          type: this.type,
          value,
          existingCodes: this.existingCodes,
        });
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        return;
      }

      this.$emit("generated", coupons);
      this.show = false;
    },

    submit() {
      this.generate();
    },

    cancel() {
      this.show = false;
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped></style>
